import { useState } from "react";
import QRCode from "react-qr-code";
import { Check, Copy, ExternalLink, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

const copy = (text, what) => {
  navigator.clipboard.writeText(text);
  toast.success(`${what} copied`);
};

/** Hall-hire settings live here, beside the rooms they govern. */
export const HireSettings = ({ config, rooms, canWrite, onSaved }) => {
  const [values, setValues] = useState(config);
  const [busy, setBusy] = useState(false);

  const link = `${window.location.origin}/hall-hire`;
  const snippet = `<a href="${link}" target="_blank" rel="noopener"\n   style="display:inline-block;padding:12px 22px;border-radius:999px;`
    + `background:#3b4fd8;color:#fff;font:600 14px system-ui;text-decoration:none">\n  `
    + `Book a hall\n</a>`;
  const iframe = `<iframe src="${link}" title="Hall hire" width="100%" height="1100" `
    + `style="border:0"></iframe>`;

  const set = (key) => (value) => setValues({ ...values, [key]: value });
  const bands = values.cancellation_bands || [];
  const setBand = (index, key, value) => setValues({ ...values,
    cancellation_bands: bands.map((b, i) => (i === index ? { ...b, [key]: value } : b)) });
  const toggleRoom = (code) => {
    const rest = values.rooms.includes(code)
      ? values.rooms.filter((c) => c !== code) : [...values.rooms, code];
    setValues({ ...values, rooms: rest });
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/hall-hire/settings", values);
      toast.success("Hall hire settings saved");
      onSaved?.(res.data.values);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="hire-settings">
      <Panel testId="panel-hire-switch" kicker="Public requests"
             title="Let the public ask to hire a space"
             actions={canWrite && (
               <Button size="sm" onClick={save} disabled={busy} data-testid="hire-settings-save"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                   : <Save className="mr-1.5 h-3.5 w-3.5" />} Save settings
               </Button>
             )}>
        <div className="space-y-4 px-4 py-4">
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/25 p-3">
            <div>
              <div className="text-[12.5px] font-medium text-foreground">
                Accept hire requests online
              </div>
              <p className="text-[11px] text-muted-foreground">
                Publishes the request form at {link}. Requests arrive in the Requests tab and on the
                calendar as pending blocks; nothing is booked until a member of staff accepts it.
              </p>
            </div>
            <Switch checked={values.enabled} disabled={!canWrite} data-testid="hire-enabled-switch"
                    onCheckedChange={set("enabled")} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <F label="Opening paragraph">
              <Textarea rows={4} value={values.intro} disabled={!canWrite}
                        data-testid="hire-intro-input" onChange={(e) => set("intro")(e.target.value)}
                        className="bg-muted/40 text-[12px]" />
            </F>
            <F label="Conditions of hire">
              <Textarea rows={4} value={values.terms} disabled={!canWrite}
                        data-testid="hire-terms-input" onChange={(e) => set("terms")(e.target.value)}
                        className="bg-muted/40 text-[12px]" />
            </F>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <F label="Minimum notice (days)">
              <Input type="number" value={values.min_notice_days} disabled={!canWrite}
                     data-testid="hire-notice-input"
                     onChange={(e) => set("min_notice_days")(Number(e.target.value))}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="How far ahead (months)">
              <Input type="number" value={values.max_months_ahead} disabled={!canWrite}
                     data-testid="hire-ahead-input"
                     onChange={(e) => set("max_months_ahead")(Number(e.target.value))}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Contact email shown publicly">
              <Input value={values.contact_email} disabled={!canWrite}
                     data-testid="hire-contact-email-input"
                     onChange={(e) => set("contact_email")(e.target.value)}
                     className="bg-muted/40" />
            </F>
            <F label="Contact phone shown publicly">
              <Input value={values.contact_phone} disabled={!canWrite}
                     data-testid="hire-contact-phone-input"
                     onChange={(e) => set("contact_phone")(e.target.value)}
                     className="bg-muted/40" />
            </F>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/25 p-3">
            <div className="text-[12px] text-foreground">
              Show the rate card on the public page
              <p className="text-[11px] text-muted-foreground">
                Turn this off if you quote every hire individually.
              </p>
            </div>
            <Switch checked={values.show_rates} disabled={!canWrite} data-testid="hire-rates-switch"
                    onCheckedChange={set("show_rates")} />
          </div>

          <div>
            <div className="text-[12px] font-medium text-foreground">
              Which spaces can be requested
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">
              Select none to offer every bookable space. {values.rooms.length} selected.
            </p>
            <div className="flex flex-wrap gap-2">
              {rooms.map((r) => {
                const on = values.rooms.includes(r.code);
                return (
                  <button key={r.code} type="button" disabled={!canWrite}
                          onClick={() => toggleRoom(r.code)}
                          data-testid={`hire-room-toggle-${r.code}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] transition-colors duration-150 ${
                            on ? "border-primary bg-primary/15 text-primary"
                              : "border-border bg-muted/40 text-muted-foreground hover:text-foreground"}`}>
                    {on && <Check className="h-3 w-3" />}
                    {r.name}{r.room_number ? ` · ${r.room_number}` : ""} · seats {r.capacity}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Panel>

      <Panel testId="panel-hire-terms" kicker="Money terms"
             title="Deposits, cancellation charges and invoicing"
             actions={canWrite && (
               <Button size="sm" onClick={save} disabled={busy} data-testid="hire-terms-save"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                   : <Save className="mr-1.5 h-3.5 w-3.5" />} Save settings
               </Button>
             )}>
        <div className="space-y-4 px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <F label="Deposit" hint="Applied to every chargeable booking.">
              <Select value={values.deposit_type} disabled={!canWrite}
                      onValueChange={set("deposit_type")}>
                <SelectTrigger data-testid="hire-deposit-type-select" className="bg-muted/40">
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No deposit</SelectItem>
                  <SelectItem value="fixed">A fixed amount</SelectItem>
                  <SelectItem value="percent">A percentage of the charge</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label={values.deposit_type === "percent" ? "Percentage of the charge" : "Deposit amount"}>
              <Input type="number" value={values.deposit_value} disabled={!canWrite || values.deposit_type === "none"}
                     data-testid="hire-deposit-value-input"
                     onChange={(e) => set("deposit_value")(Number(e.target.value))}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Deposit due (days before)">
              <Input type="number" value={values.deposit_due_days_before} disabled={!canWrite}
                     data-testid="hire-deposit-due-input"
                     onChange={(e) => set("deposit_due_days_before")(Number(e.target.value))}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Invoice payment terms (days)">
              <Input type="number" value={values.invoice_terms_days} disabled={!canWrite}
                     data-testid="hire-invoice-terms-input"
                     onChange={(e) => set("invoice_terms_days")(Number(e.target.value))}
                     className="bg-muted/40 font-mono" />
            </F>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/25 p-3">
            <div className="text-[12px] text-foreground">
              Deposits are refundable
              <p className="text-[11px] text-muted-foreground">
                When on, staff settle the deposit after the event and may keep back a stated amount
                for damage or extra cleaning — which posts as hire income.
              </p>
            </div>
            <Switch checked={values.deposit_refundable} disabled={!canWrite}
                    data-testid="hire-deposit-refundable-switch"
                    onCheckedChange={set("deposit_refundable")} />
          </div>

          <div>
            <div className="text-[12px] font-medium text-foreground">Cancellation charges by notice</div>
            <p className="mb-2 text-[11px] text-muted-foreground">
              The first band the hirer qualifies for applies. Anything shorter than the last band is
              charged in full.
            </p>
            <div className="space-y-2">
              {bands.map((band, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                  <F label="Notice of at least (days)">
                    <Input type="number" value={band.days_before} disabled={!canWrite}
                           data-testid={`hire-band-days-${index}`}
                           onChange={(e) => setBand(index, "days_before", Number(e.target.value))}
                           className="bg-muted/40 font-mono" />
                  </F>
                  <F label="Charge (% of hire)">
                    <Input type="number" value={band.charge_percent} disabled={!canWrite}
                           data-testid={`hire-band-percent-${index}`}
                           onChange={(e) => setBand(index, "charge_percent", Number(e.target.value))}
                           className="bg-muted/40 font-mono" />
                  </F>
                  {canWrite && (
                    <Button size="sm" variant="outline" data-testid={`hire-band-remove-${index}`}
                            onClick={() => setValues({ ...values,
                              cancellation_bands: bands.filter((_, i) => i !== index) })}
                            className="h-9 rounded-full border-border px-3 text-[10.5px]">Remove</Button>
                  )}
                </div>
              ))}
            </div>
            {canWrite && (
              <Button size="sm" variant="outline" data-testid="hire-band-add"
                      onClick={() => setValues({ ...values,
                        cancellation_bands: [...bands, { days_before: 0, charge_percent: 100 }] })}
                      className="mt-2 h-8 rounded-full border-border px-3 text-[10.5px]">
                Add a band</Button>
            )}
          </div>

          <F label="Note printed on every hire invoice">
            <Textarea rows={2} value={values.invoice_footer} disabled={!canWrite}
                      data-testid="hire-invoice-footer-input"
                      onChange={(e) => set("invoice_footer")(e.target.value)}
                      className="bg-muted/40 text-[12px]" />
          </F>

          <F label="Terms printed on the hire agreement"
             hint="One rule per line. Changing these stamps a new version on every agreement raised afterwards.">
            <Textarea rows={4} value={values.agreement_terms} disabled={!canWrite}
                      data-testid="hire-agreement-terms-input"
                      onChange={(e) => set("agreement_terms")(e.target.value)}
                      className="bg-muted/40 text-[12px]" />
          </F>

          <div className="grid gap-4 sm:grid-cols-3">
            <F label="Remind this many days before due"
               hint="Comma separated, e.g. 7, 2">
              <Input value={(values.reminder_days_before || []).join(", ")} disabled={!canWrite}
                     data-testid="hire-reminder-days-input"
                     onChange={(e) => set("reminder_days_before")(
                       e.target.value.split(",").map((d) => parseInt(d.trim(), 10))
                         .filter((d) => !Number.isNaN(d)))}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Chase again every (days, once overdue)">
              <Input type="number" value={values.overdue_every_days} disabled={!canWrite}
                     data-testid="hire-overdue-every-input"
                     onChange={(e) => set("overdue_every_days")(Number(e.target.value))}
                     className="bg-muted/40 font-mono" />
            </F>
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/25 p-3">
              <div className="text-[12px] text-foreground">Chase overdue money</div>
              <Switch checked={values.chase_overdue} disabled={!canWrite}
                      data-testid="hire-chase-overdue-switch"
                      onCheckedChange={set("chase_overdue")} />
            </div>
          </div>
        </div>
      </Panel>

      <Panel testId="panel-hire-embed" kicker="Put it on your website"
             title="Link, QR code and copy-paste snippets">
        <div className="grid gap-4 px-4 py-4 lg:grid-cols-[1fr_180px]">
          <div className="space-y-3">
            <F label="Public link">
              <div className="flex gap-2">
                <Input readOnly value={link} data-testid="hire-public-link"
                       className="bg-muted/40 font-mono text-[11.5px]" />
                <Button size="sm" variant="outline" onClick={() => copy(link, "Link")}
                        data-testid="hire-copy-link" className="h-9 rounded-full border-border px-3">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" asChild
                        className="h-9 rounded-full border-border px-3">
                  <a href={link} target="_blank" rel="noopener noreferrer"
                     data-testid="hire-open-link"><ExternalLink className="h-3.5 w-3.5" /></a>
                </Button>
              </div>
            </F>
            <F label="Button for your website" hint="Paste this wherever you edit your own pages.">
              <Textarea readOnly rows={4} value={snippet} data-testid="hire-embed-button"
                        onClick={() => copy(snippet, "Button snippet")}
                        className="cursor-pointer bg-muted/40 font-mono text-[10.5px]" />
            </F>
            <F label="Embed the whole form" hint="Click to copy. Works in any content manager that allows an iframe.">
              <Textarea readOnly rows={2} value={iframe} data-testid="hire-embed-iframe"
                        onClick={() => copy(iframe, "Embed snippet")}
                        className="cursor-pointer bg-muted/40 font-mono text-[10.5px]" />
            </F>
          </div>
          <div className="space-y-2">
            <div className="rounded-xl border border-border bg-white p-3">
              <QRCode value={link} size={150} style={{ width: "100%", height: "auto" }}
                      data-testid="hire-qr" />
            </div>
            <p className="text-[10.5px] leading-relaxed text-muted-foreground">
              Print this on your noticeboard or hall poster.
            </p>
          </div>
        </div>
      </Panel>
    </div>
  );
};
